import { useState } from 'react';
import { Link } from 'react-router';
import { useRegister } from '../auth/session';
import { AuthLayout } from './AuthLayout';
import { Banner, Button, Field, TextInput } from '../components/ui';
import { errorMessage, fieldErrors } from '../api/problems';

const MINIMUM_PASSWORD_LENGTH = 12;

export function RegisterPage() {
  const register = useRegister();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');

  const errors = fieldErrors(register.error);

  if (register.isSuccess) {
    return (
      <AuthLayout title="Check your email" subtitle="Almost there.">
        <Banner tone="success">
          If that address needs action, a confirmation link is on its way. Open it to finish
          setting up your drawer.
        </Banner>
        <Link to="/login" className="text-sm text-accent hover:underline">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create an account"
      subtitle="A drawer for the scripts you keep losing."
      footer={
        <>
          Already have one?{' '}
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          register.mutate({ email, displayName, password });
        }}
      >
        {/* A failure with no field attached — antiforgery, rate limiting, the API being down —
            must still say something. Rendering only field errors makes the button look dead. */}
        {register.isError && Object.keys(errors).length === 0 && (
          <Banner tone="error">{errorMessage(register.error)}</Banner>
        )}

        <Field label="Email" error={errors['Email']?.[0]}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="email"
              autoComplete="email"
              required
              invalid={Boolean(errors['Email'])}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label="Display name" error={errors['DisplayName']?.[0]}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              autoComplete="nickname"
              required
              invalid={Boolean(errors['DisplayName'])}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Password"
          hint={`At least ${MINIMUM_PASSWORD_LENGTH} characters. A passphrase beats a puzzle.`}
          error={errors['Password']?.[0]}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="new-password"
              required
              minLength={MINIMUM_PASSWORD_LENGTH}
              invalid={Boolean(errors['Password'])}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" disabled={register.isPending}>
          {register.isPending ? 'Creating…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
